// frontend/src/pages/CadastroLocatarios.js
import React, { useState, useEffect } from "react";
import "./CadastroImoveis.css";
import BuscarMatricula from "../components/BuscarMatricula";
import axios from "axios";
import { FaFilter, FaCopy, FaFileCsv, FaEdit, FaTrash, FaEye, FaTimes, FaSave } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import { mascararCnpj } from "../utils/documento";
import DocumentoEstrangeiroCamposHtml, { FlagEstrangeiroHtml } from "../components/DocumentoEstrangeiroCamposHtml";
import {
  DOCUMENTO_ESTRANGEIRO_VAZIO,
  camposDocumentoEstrangeiro,
  descreverDocumentoEstrangeiro,
  validarDocumentoEstrangeiro,
} from "../utils/documentoEstrangeiro";

// =========================
// Máscaras
// =========================
const maskCPF = (value) => {
  return (value ?? "")
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    .substring(0, 14);
};

// CNPJ numérico ou alfanumérico (letras A-Z nas 12 primeiras posições).
const maskCNPJ = (value) => mascararCnpj(value);

const maskPhone = (value) => {
  // Fixo (10 dígitos): (00) 0000-0000 · Celular (11 dígitos): (00) 00000-0000.
  const d = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const maskCell = (value) => {
  return (value ?? "")
    .replace(/\D/g, "")
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .substring(0, 15);
};

// =========================
// Datas
// =========================
const formatDateInput = (value) => {
  if (!value) return "";
  if (typeof value === "string" && value.includes("T")) return value.split("T")[0];
  return value;
};

const formatDateBR = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toLocaleDateString("pt-BR");
};

// =========================
// Helpers de payload
// =========================
const isBlank = (v) => typeof v === "string" && v.trim() === "";

const limparPayload = (obj) => {
  const out = { ...obj };
  Object.keys(out).forEach((k) => {
    const v = out[k];
    if (v === undefined || v === null) delete out[k];
    else if (typeof v === "string" && v.trim() === "") delete out[k];
  });
  return out;
};

const normalizarMatriculaId = (v) => {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    if (v.trim() === "") return undefined;
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
};

// ✅ NOVO (mínimo): garantir "MT007" a partir de "MT007 - IRP"
const extrairCodigoMatriculaPuro = (valor) => {
  const s = String(valor ?? "").trim();
  if (!s) return "";
  return s.split("-")[0].trim();
};

// =========================
// Payloads
// =========================
const montarPayloadCriacao = (form) => {
  const payloadBase = {
    codigoLocatario: form.codigoLocatario,
    apelido: form.apelido,
    nomeLocatario: form.nomeLocatario,
    status: form.status,
    dataInicio: form.dataInicio,
    dataFim: form.dataFim,
    tipoPessoa: form.tipoPessoa,
    cpfCnpj: form.cpfCnpj,
    observacoes: form.observacoes,
    ...camposDocumentoEstrangeiro(form),
  };

  const matriculaIdNum = normalizarMatriculaId(form.matriculaId);
  if (matriculaIdNum !== undefined) payloadBase.matriculaId = matriculaIdNum;

  const codigoPuro = extrairCodigoMatriculaPuro(form.codigoMatricula);
  if (!isBlank(codigoPuro)) payloadBase.codigoMatricula = codigoPuro;

  const contatos = [
    {
      nome: form.nomeContato1,
      celular: form.celularContato1,
      telefone: form.telefoneContato1,
      email: form.emailContato1,
    },
    {
      nome: form.nomeContato2,
      celular: form.celularContato2,
      telefone: form.telefoneContato2,
      email: form.emailContato2,
    },
    {
      nome: form.nomeContato3,
      celular: form.celularContato3,
      telefone: form.telefoneContato3,
      email: form.emailContato3,
    },
  ]
    .map(limparPayload)
    .filter((c) => Object.keys(c).length > 0);

  const payload = limparPayload(payloadBase);
  if (contatos.length > 0) payload.contatos = contatos;

  return payload;
};

const montarPayloadUpdate = (form) => {
  const payloadBase = {
    codigoLocatario: form.codigoLocatario,
    apelido: form.apelido,
    nomeLocatario: form.nomeLocatario,
    status: form.status,
    dataInicio: form.dataInicio,
    dataFim: form.dataFim,
    tipoPessoa: form.tipoPessoa,
    cpfCnpj: form.cpfCnpj,
    observacoes: form.observacoes,
    ...camposDocumentoEstrangeiro(form),
  };

  const matriculaIdNum = normalizarMatriculaId(form.matriculaId);
  if (matriculaIdNum !== undefined) payloadBase.matriculaId = matriculaIdNum;

  const codigoPuro = extrairCodigoMatriculaPuro(form.codigoMatricula);
  if (!isBlank(codigoPuro)) payloadBase.codigoMatricula = codigoPuro;

  return limparPayload(payloadBase);
};

function CadastroLocatarios() {
  const defaultForm = {
    codigoLocatario: "",
    apelido: "",
    nomeLocatario: "",
    status: "ATIVO",
    dataInicio: "",
    dataFim: "",
    tipoPessoa: "Física",
    cpfCnpj: "",
    observacoes: "",
    nomeContato1: "",
    celularContato1: "",
    telefoneContato1: "",
    emailContato1: "",
    nomeContato2: "",
    celularContato2: "",
    telefoneContato2: "",
    emailContato2: "",
    nomeContato3: "",
    celularContato3: "",
    telefoneContato3: "",
    emailContato3: "",
    matriculaId: "",
    codigoMatricula: "",
    ...DOCUMENTO_ESTRANGEIRO_VAZIO,
  };

  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("locatariosGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("locatariosGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    const decoded = jwtDecode(token);
    tipoUsuario = decoded.tipo
      ?.toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  const [form, setForm] = useState(defaultForm);
  const [registros, setRegistros] = useState([]);
  const [filtrados, setFiltrados] = useState([]);
  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(true);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const registrosPorPagina = 10;

  const indexInicial = (paginaAtual - 1) * registrosPorPagina;
  const indexFinal = indexInicial + registrosPorPagina;
  const registrosPaginados = filtrados.slice(indexInicial, indexFinal);
  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / registrosPorPagina));

  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [codigoVisualizando, setCodigoVisualizando] = useState(null);
  const [editandoCodigo, setEditandoCodigo] = useState(null);

  const formTravado = modoVisualizacao && !editandoCodigo;
  const podeMostrarExcluirNoForm = modoVisualizacao && !editandoCodigo && Boolean(codigoVisualizando);

  const [mostrarPendencias, setMostrarPendencias] = useState(false);

  const estaVazio = (v) => !String(v ?? "").trim();

  const camposOpcionais = [
    "observacoes",
    "telefoneContato1",
    "nomeContato2",
    "celularContato2",
    "telefoneContato2",
    "emailContato2",
    "nomeContato3",
    "celularContato3",
    "telefoneContato3",
    "emailContato3",
    "matriculaId",
    "codigoMatricula",
  ];

  const classeCampoOpcional = (campo) => {
    if (!mostrarPendencias) return "";
    if (!camposOpcionais.includes(campo)) return "";

    if (campo === "matriculaId") {
      return !String(form.matriculaId ?? "").trim() ? "campo-pendente" : "";
    }

    return estaVazio(form[campo]) ? "campo-pendente" : "";
  };

  const classeCampoFormulario = (extra = "") => `campo-formulario-padrao ${extra}`.trim();

  const classeCampoFormularioOpcional = (campo, extra = "") => `campo-formulario-padrao ${classeCampoOpcional(campo)} ${extra}`.trim();

  const resetForm = () => {
    setForm(defaultForm);
    setModoVisualizacao(false);
    setCodigoVisualizando(null);
    setEditandoCodigo(null);
    setMostrarPendencias(false);
  };

  useEffect(() => {
    const tk = localStorage.getItem("token");
    const headers = tk ? { Authorization: `Bearer ${tk}` } : {};

    async function carregarLocatarios() {
      try {
        const res = await axios.get("/api/locatarios", { headers });
        const lista = Array.isArray(res.data) ? res.data : [];
        setRegistros(lista);
        setFiltrados(lista);
      } catch (err) {
        console.error("Erro ao carregar locatários:", err);
      } finally {
        setCarregando(false);
      }
    }

    carregarLocatarios();
  }, []);

  const recarregarLista = async () => {
    const tk = localStorage.getItem("token");
    const headers = tk ? { Authorization: `Bearer ${tk}` } : {};
    const res = await axios.get("/api/locatarios", { headers });
    const lista = Array.isArray(res.data) ? res.data : [];
    setRegistros(lista);
    setFiltrados(lista);
  };

  const handleBusca = (e) => {
    const valor = e.target.value;
    setBusca(valor);

    if (!valor.trim()) {
      setFiltrados(registros);
      setPaginaAtual(1);
      return;
    }

    const termos = valor
      .toLowerCase()
      .split(/[,\.]+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const lista = registros.filter((r) => {
      const texto = [
        r.codigoLocatario,
        r.apelido,
        r.nomeLocatario,
        r.tipoPessoa,
        r.cpfCnpj || descreverDocumentoEstrangeiro(r),
        r.dataInicio,
        r.dataFim,
        r.observacoes,
        r.codigoMatricula,
        r.matriculaId,
        ...(r.contatos || []).flatMap((c) => [c.nome, c.celular, c.telefone, c.email]),
      ]
        .join(" ")
        .toLowerCase();

      return termos.every((t) => texto.includes(t));
    });

    setFiltrados(lista);
    setPaginaAtual(1);
  };

  const limparFiltro = () => {
    setBusca("");
    setFiltrados(registros);
    setPaginaAtual(1);
  };

  const copiarTabela = async () => {
    const texto = filtrados
      .map((r) => {
        const c1 = r.contatos?.[0] ?? {};
        const c2 = r.contatos?.[1] ?? {};
        const c3 = r.contatos?.[2] ?? {};

        return `${r.codigoLocatario ?? ""}\t${r.apelido ?? ""}\t${r.nomeLocatario ?? ""}\t${
          r.tipoPessoa ?? ""
        }\t${r.cpfCnpj || descreverDocumentoEstrangeiro(r)}\t${r.dataInicio ?? ""}\t${r.dataFim ?? ""}\t${c1.nome ?? ""}\t${
          c1.celular ?? ""
        }\t${c1.telefone ?? ""}\t${c1.email ?? ""}\t${c2.nome ?? ""}\t${c2.celular ?? ""}\t${
          c2.telefone ?? ""
        }\t${c2.email ?? ""}\t${c3.nome ?? ""}\t${c3.celular ?? ""}\t${c3.telefone ?? ""}\t${c3.email ?? ""}\t${r.codigoMatricula ?? ""}`;
      })
      .join("\n");

    const ok = await copiarTexto(texto);
    alert(ok ? "Registros copiados para a área de transferência!" : "Não foi possível copiar.");
  };

  const exportarCSV = () => {
    const BOM = "\uFEFF";

    const header = [
      "Código",
      "Apelido",
      "Nome",
      "Tipo Pessoa",
      "CPF/CNPJ",
      "Data Início",
      "Data Fim",
      "Observações",
      "Nome contato 1",
      "Celular contato 1",
      "Telefone contato 1",
      "Email contato 1",
      "Nome contato 2",
      "Celular contato 2",
      "Telefone contato 2",
      "Email contato 2",
      "Nome contato 3",
      "Celular contato 3",
      "Telefone contato 3",
      "Email contato 3",
      "Matrícula",
    ];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const excelText = (value) => `="${String(value ?? "")}"`;

    const linhas = (filtrados || []).map((r) => {
      const c1 = r.contatos?.[0] ?? {};
      const c2 = r.contatos?.[1] ?? {};
      const c3 = r.contatos?.[2] ?? {};

      return [
        r.codigoLocatario ?? "",
        r.apelido ?? "",
        r.nomeLocatario ?? "",
        r.tipoPessoa ?? "",
        excelText(r.cpfCnpj || descreverDocumentoEstrangeiro(r)),
        excelText(formatDateBR(r.dataInicio)),
        excelText(formatDateBR(r.dataFim)),
        r.observacoes ?? "",
        c1.nome ?? "",
        excelText(c1.celular ?? ""),
        excelText(c1.telefone ?? ""),
        c1.email ?? "",
        c2.nome ?? "",
        excelText(c2.celular ?? ""),
        excelText(c2.telefone ?? ""),
        c2.email ?? "",
        c3.nome ?? "",
        excelText(c3.celular ?? ""),
        excelText(c3.telefone ?? ""),
        c3.email ?? "",
        excelText(r.codigoMatricula ?? ""),
      ].map(csvEscape);
    });

    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "locatarios.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const preencherForm = (item) => {
    const c1 = item.contatos?.[0] ?? {};
    const c2 = item.contatos?.[1] ?? {};
    const c3 = item.contatos?.[2] ?? {};

    setForm({
      codigoLocatario: item.codigoLocatario ?? "",
      apelido: item.apelido ?? "",
      nomeLocatario: item.nomeLocatario ?? "",
      status: item.status ?? "ATIVO",
      dataInicio: formatDateInput(item.dataInicio),
      dataFim: formatDateInput(item.dataFim),
      // O backend grava em MAIÚSCULO ("FÍSICA"/"JURÍDICA"); a tela usa "Física"/"Jurídica".
      tipoPessoa: String(item.tipoPessoa || "").toLocaleUpperCase("pt-BR") === "JURÍDICA" ? "Jurídica" : "Física",
      cpfCnpj: item.cpfCnpj ?? "",
      estrangeiro: Boolean(item.estrangeiro),
      documentoEstrangeiroTipo: item.documentoEstrangeiroTipo ?? "",
      documentoEstrangeiroNumero: item.documentoEstrangeiroNumero ?? "",
      documentoEstrangeiroPais: item.documentoEstrangeiroPais ?? "",
      observacoes: item.observacoes ?? "",
      nomeContato1: c1.nome ?? "",
      celularContato1: c1.celular ?? "",
      telefoneContato1: c1.telefone ?? "",
      emailContato1: c1.email ?? "",
      nomeContato2: c2.nome ?? "",
      celularContato2: c2.celular ?? "",
      telefoneContato2: c2.telefone ?? "",
      emailContato2: c2.email ?? "",
      nomeContato3: c3.nome ?? "",
      celularContato3: c3.celular ?? "",
      telefoneContato3: c3.telefone ?? "",
      emailContato3: c3.email ?? "",
      matriculaId: item.matriculaId ?? "",
      codigoMatricula: extrairCodigoMatriculaPuro(item.codigoMatricula ?? ""),
    });
  };

  const visualizarRegistro = (item) => {
    if (!podeVisualizar) return alert("Você não tem permissão para visualizar.");

    preencherForm(item);

    setCodigoVisualizando(item.codigoLocatario ?? null);
    setEditandoCodigo(null);
    setModoVisualizacao(true);

    setMostrarPendencias(true);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const excluirRegistro = async (codigoLocatario) => {
    if (!podeExcluir) return alert("Você não tem permissão para excluir.");
    if (!window.confirm("Tem certeza que deseja excluir este locatário?")) return;

    try {
      const tk = localStorage.getItem("token");
      const headers = tk ? { Authorization: `Bearer ${tk}` } : {};

      await axios.delete(`/api/locatarios/${codigoLocatario}`, { headers });

      alert("Locatário excluído!");
      const restantes = registros.filter((r) => r.codigoLocatario !== codigoLocatario);

      setRegistros(restantes);
      setFiltrados(restantes);
      setPaginaAtual(1);
      resetForm();
    } catch (err) {
      console.error("Erro ao excluir:", err);
      alert("Erro ao excluir locatário.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMostrarPendencias(true);

    if (!editandoCodigo && !podeCadastrar) return alert("Você não tem permissão para cadastrar.");
    if (editandoCodigo && !podeEditar) return alert("Você não tem permissão para editar.");

    // Campos do locatário são opcionais. Só o locatário estrangeiro sem CPF/CNPJ
    // precisa informar o documento estrangeiro.
    const errosEstrangeiro = Object.values(validarDocumentoEstrangeiro(form, { temDocumentoNacional: !estaVazio(form.cpfCnpj) }));
    if (errosEstrangeiro.length) {
      alert(errosEstrangeiro[0]);
      return;
    }

    try {
      const tk = localStorage.getItem("token");
      const headers = tk ? { Authorization: `Bearer ${tk}` } : {};

      if (editandoCodigo) {
        const codigoParaAtualizar = editandoCodigo || form.codigoLocatario;
        if (!codigoParaAtualizar) {
          alert("Código do locatário inválido para atualizar.");
          return;
        }

        const payload = montarPayloadUpdate(form);
        await axios.put(`/api/locatarios/${codigoParaAtualizar}`, payload, { headers });
        alert("Locatário atualizado com sucesso!");
      } else {
        const payload = montarPayloadCriacao(form);
        await axios.post("/api/locatarios", payload, { headers });
        alert("Locatário cadastrado com sucesso!");
      }

      await recarregarLista();
      setPaginaAtual(1);
      resetForm();
    } catch (err) {
      console.error("Erro ao salvar (status):", err?.response?.status);
      console.error("Erro ao salvar (data):", JSON.stringify(err?.response?.data, null, 2));
      console.error("Erro ao salvar (full):", err);

      alert(err?.response?.data?.mensagem || err?.response?.data?.erro || err?.response?.data?.error || "Erro ao salvar locatário.");
    }
  };

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(1);
  }, [paginaAtual, totalPaginas]);

  return (
    <div className="local-container">
      <style>{`
        .campo-formulario-padrao,
        .campo-formulario-padrao:focus,
        .campo-formulario-padrao:active,
        .campo-formulario-padrao:read-only,
        .campo-formulario-padrao:disabled {
          background: transparent !important;
          background-color: transparent !important;
          color: var(--ig-t-ffffff) !important;
          box-shadow: none !important;
          -webkit-text-fill-color: var(--ig-t-ffffff) !important;
          opacity: 1 !important;
        }

        .campo-formulario-padrao::placeholder {
          color: var(--ig-t-255-255-255-a072) !important;
          -webkit-text-fill-color: var(--ig-t-255-255-255-a072) !important;
        }

        .campo-formulario-padrao::-webkit-calendar-picker-indicator {
          filter: var(--ig-x-11pexw5);
          opacity: 0.9;
          cursor: pointer;
        }
      `}</style>

      <h2>Locatário</h2>

      <div className="filtro-barra">
        <input type="text" placeholder="Filtrar Locatário" value={busca} onChange={handleBusca} disabled={!podeVisualizar} />
        <div className="filtro-botoes">
          <button onClick={limparFiltro} className="btn-acao" disabled={!podeVisualizar} type="button">
            <FaFilter /> Limpar Filtro
          </button>
          <button onClick={copiarTabela} className="btn-acao" disabled={!podeVisualizar} type="button">
            <FaCopy /> Copiar
          </button>
          <button onClick={exportarCSV} className="btn-acao" disabled={!podeVisualizar} type="button">
            <FaFileCsv /> CSV
          </button>
        </div>
      </div>

      <div className="grid-toggle-area right">
        {!gridMinimizado ? (
          <button
            type="button"
            className="btn-grid-toggle small danger"
            onClick={() => setGridMinimizado(true)}
            title="Minimizar o Grid"
            aria-label="Minimizar o Grid"
            disabled={!podeVisualizar}
          >
            ↘ Minimizar
          </button>
        ) : (
          <button
            type="button"
            className="btn-grid-toggle small outline"
            onClick={() => setGridMinimizado(false)}
            title="Expandir o Grid"
            aria-label="Expandir o Grid"
            disabled={!podeVisualizar}
          >
            ↗ Expandir
          </button>
        )}
      </div>

      {!gridMinimizado && (
        <div className="bloco-lista">
          {carregando ? (
            <p>Carregando...</p>
          ) : filtrados.length === 0 ? (
            <p>Nenhum Locatário encontrado</p>
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Apelido</th>
                    <th>Nome</th>
                    <th>Status</th>
                    <th>Tipo Pessoa</th>
                    <th>Data Início</th>
                    <th>Data Final</th>
                    <th>Ações</th>
                  </tr>
                </thead>

                <tbody>
                  {registrosPaginados.map((item) => (
                    <tr key={item.codigoLocatario ?? item.id}>
                      <td>{item.codigoLocatario}</td>
                      <td>{item.apelido}</td>
                      <td>{item.nomeLocatario}</td>
                      <td>{item.status}</td>
                      <td>{item.tipoPessoa}</td>
                      <td>{formatDateBR(item.dataInicio)}</td>
                      <td>{formatDateBR(item.dataFim)}</td>

                      <td className="col-acoes">
                        {podeVisualizar && (
                          <button className="btn-editar" onClick={() => visualizarRegistro(item)} type="button">
                            <FaEye /> Visualizar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="rodape-tabela">
                <span>Locatários por página: {registrosPorPagina}</span>
                <span>
                  {indexInicial + 1}-{Math.min(indexFinal, filtrados.length)} de {filtrados.length}
                </span>

                <div className="paginacao">
                  <span onClick={() => setPaginaAtual(1)}>&laquo;</span>
                  <span onClick={() => setPaginaAtual((p) => Math.max(p - 1, 1))}>&lt;</span>

                  {Array.from({ length: totalPaginas }).map((_, i) => (
                    <span key={i} className={paginaAtual === i + 1 ? "ativo" : ""} onClick={() => setPaginaAtual(i + 1)}>
                      {i + 1}
                    </span>
                  ))}

                  <span onClick={() => setPaginaAtual((p) => Math.min(p + 1, totalPaginas))}>&gt;</span>
                  <span onClick={() => setPaginaAtual(totalPaginas)}>&raquo;</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {podeCadastrar || podeVisualizar ? (
        <div className="bloco-formulario">
          <form className="form-grid" onSubmit={handleSubmit}>
            <div className="form-row">
              <label>
                Cód Locatário
                <input name="codigoLocatario" value={form.codigoLocatario ?? ""} disabled className={classeCampoFormulario()} />
              </label>
            </div>

            <div className="form-row">
              <label>
                Apelido Usuário/Locatário*
                <input
                  name="apelido"
                  value={form.apelido ?? ""}
                  onChange={(e) => setForm({ ...form, apelido: e.target.value })}
                  disabled={formTravado}
                  className={classeCampoFormulario()}
                />
              </label>

              <label>
                Nome Usuário/Locatário*
                <input
                  name="nomeLocatario"
                  value={form.nomeLocatario ?? ""}
                  onChange={(e) => setForm({ ...form, nomeLocatario: e.target.value })}
                  disabled={formTravado}
                  className={classeCampoFormulario()}
                />
              </label>

              <label>
                Status*
                <select name="status" value={form.status ?? "ATIVO"} onChange={(e) => setForm({ ...form, status: e.target.value })} disabled={formTravado}>
                  <option value="ATIVO">ATIVO</option>
                  <option value="INATIVO">INATIVO</option>
                </select>
              </label>
            </div>

            <div className="form-row">
              <label>
                Data Início do Contrato*
                <input
                  type="date"
                  name="dataInicio"
                  value={form.dataInicio ?? ""}
                  onChange={(e) => setForm({ ...form, dataInicio: e.target.value })}
                  disabled={formTravado}
                  className={classeCampoFormulario()}
                />
              </label>

              <label>
                Data Final do Contrato*
                <input
                  type="date"
                  name="dataFim"
                  value={form.dataFim ?? ""}
                  onChange={(e) => setForm({ ...form, dataFim: e.target.value })}
                  disabled={formTravado}
                  className={classeCampoFormulario()}
                />
              </label>

              <label>
                Observações
                <textarea
                  name="observacoes"
                  rows="2"
                  value={form.observacoes ?? ""}
                  onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                  disabled={formTravado}
                  className={classeCampoFormularioOpcional("observacoes")}
                />
              </label>
            </div>

            <div className="form-row">
              <label>Pessoa</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="tipoPessoa"
                    value="Física"
                    checked={form.tipoPessoa === "Física"}
                    onChange={(e) => setForm({ ...form, tipoPessoa: e.target.value, cpfCnpj: "" })}
                    disabled={formTravado}
                  />
                  Física
                </label>

                <label>
                  <input
                    type="radio"
                    name="tipoPessoa"
                    value="Jurídica"
                    checked={form.tipoPessoa === "Jurídica"}
                    onChange={(e) => setForm({ ...form, tipoPessoa: e.target.value, cpfCnpj: "" })}
                    disabled={formTravado}
                  />
                  Jurídica
                </label>
              </div>
            </div>

            <div className="form-row" style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
              <label style={{ flex: "1 1 260px" }}>
                {form.tipoPessoa === "Física"
                  ? form.estrangeiro
                    ? "Informe o CPF (opcional)"
                    : "Informe o CPF*"
                  : form.estrangeiro
                    ? "Informe o CNPJ (opcional)"
                    : "Informe o CNPJ*"}
                <input
                  name="cpfCnpj"
                  value={form.cpfCnpj ?? ""}
                  onChange={(e) => {
                    if (formTravado) return;
                    const v = e.target.value;
                    const masked = form.tipoPessoa === "Física" ? maskCPF(v) : maskCNPJ(v);
                    setForm({ ...form, cpfCnpj: masked });
                  }}
                  disabled={formTravado}
                  className={classeCampoFormulario()}
                />
              </label>
              <div style={{ paddingBottom: 10 }}>
                <FlagEstrangeiroHtml
                  valor={form}
                  onChange={(campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }))}
                  somenteLeitura={formTravado}
                  rotulo="Locatário estrangeiro"
                />
              </div>
            </div>

            {form.estrangeiro && (
              <div className="form-row">
                <DocumentoEstrangeiroCamposHtml
                  obrigatorio={estaVazio(form.cpfCnpj)}
                  mostrarFlag={false}
                  valor={form}
                  onChange={(campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }))}
                  somenteLeitura={formTravado}
                  classeCampo={classeCampoFormulario()}
                  classeLinha=""
                />
              </div>
            )}

            <h4>Contato 1</h4>
            <div className="form-row">
              <label>
                Nome Contato 1*
                <input
                  name="nomeContato1"
                  value={form.nomeContato1 ?? ""}
                  onChange={(e) => setForm({ ...form, nomeContato1: e.target.value })}
                  disabled={formTravado}
                  className={classeCampoFormulario()}
                />
              </label>

              <label>
                Celular Contato 1*
                <input
                  name="celularContato1"
                  value={form.celularContato1 ?? ""}
                  onChange={(e) => setForm({ ...form, celularContato1: maskCell(e.target.value) })}
                  disabled={formTravado}
                  className={classeCampoFormulario()}
                />
              </label>

              <label>
                Telefone Contato 1
                <input
                  name="telefoneContato1"
                  value={form.telefoneContato1 ?? ""}
                  onChange={(e) => setForm({ ...form, telefoneContato1: maskPhone(e.target.value) })}
                  disabled={formTravado}
                  className={classeCampoFormularioOpcional("telefoneContato1")}
                />
              </label>

              <label>
                E-mail Contato 1*
                <input
                  name="emailContato1"
                  value={form.emailContato1 ?? ""}
                  onChange={(e) => setForm({ ...form, emailContato1: e.target.value })}
                  disabled={formTravado}
                  className={classeCampoFormulario()}
                />
              </label>
            </div>

            <h4>Contato 2</h4>
            <div className="form-row">
              <label>
                Nome Contato 2
                <input
                  name="nomeContato2"
                  value={form.nomeContato2 ?? ""}
                  onChange={(e) => setForm({ ...form, nomeContato2: e.target.value })}
                  disabled={formTravado}
                  className={classeCampoFormularioOpcional("nomeContato2")}
                />
              </label>

              <label>
                Celular Contato 2
                <input
                  name="celularContato2"
                  value={form.celularContato2 ?? ""}
                  onChange={(e) => setForm({ ...form, celularContato2: maskCell(e.target.value) })}
                  disabled={formTravado}
                  className={classeCampoFormularioOpcional("celularContato2")}
                />
              </label>

              <label>
                Telefone Contato 2
                <input
                  name="telefoneContato2"
                  value={form.telefoneContato2 ?? ""}
                  onChange={(e) => setForm({ ...form, telefoneContato2: maskPhone(e.target.value) })}
                  disabled={formTravado}
                  className={classeCampoFormularioOpcional("telefoneContato2")}
                />
              </label>

              <label>
                E-mail Contato 2
                <input
                  name="emailContato2"
                  value={form.emailContato2 ?? ""}
                  onChange={(e) => setForm({ ...form, emailContato2: e.target.value })}
                  disabled={formTravado}
                  className={classeCampoFormularioOpcional("emailContato2")}
                />
              </label>
            </div>

            <h4>Contato 3</h4>
            <div className="form-row">
              <label>
                Nome Contato 3
                <input
                  name="nomeContato3"
                  value={form.nomeContato3 ?? ""}
                  onChange={(e) => setForm({ ...form, nomeContato3: e.target.value })}
                  disabled={formTravado}
                  className={classeCampoFormularioOpcional("nomeContato3")}
                />
              </label>

              <label>
                Celular Contato 3
                <input
                  name="celularContato3"
                  value={form.celularContato3 ?? ""}
                  onChange={(e) => setForm({ ...form, celularContato3: maskCell(e.target.value) })}
                  disabled={formTravado}
                  className={classeCampoFormularioOpcional("celularContato3")}
                />
              </label>

              <label>
                Telefone Contato 3
                <input
                  name="telefoneContato3"
                  value={form.telefoneContato3 ?? ""}
                  onChange={(e) => setForm({ ...form, telefoneContato3: maskPhone(e.target.value) })}
                  disabled={formTravado}
                  className={classeCampoFormularioOpcional("telefoneContato3")}
                />
              </label>

              <label>
                E-mail Contato 3
                <input
                  name="emailContato3"
                  value={form.emailContato3 ?? ""}
                  onChange={(e) => setForm({ ...form, emailContato3: e.target.value })}
                  disabled={formTravado}
                  className={classeCampoFormularioOpcional("emailContato3")}
                />
              </label>
            </div>

            <div className="form-row">
              <label>
                Matrícula
                {formTravado ? (
                  <input value={form.codigoMatricula ?? ""} disabled className={classeCampoFormularioOpcional("codigoMatricula")} />
                ) : (
                  <div className={classeCampoOpcional("matriculaId")}>
                    <BuscarMatricula
                      key={`matricula-${editandoCodigo || codigoVisualizando || "novo"}`}
                      valorInicial={form.codigoMatricula || ""}
                      onSelecionar={(matricula) =>
                        setForm((prev) => ({
                          ...prev,
                          matriculaId: matricula?.id ?? "",
                          codigoMatricula: extrairCodigoMatriculaPuro(matricula?.codigoMatricula ?? ""),
                        }))
                      }
                      disabled={formTravado}
                    />
                  </div>
                )}
              </label>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              {modoVisualizacao && !editandoCodigo && podeEditar && (
                <button
                  type="button"
                  className="btn-editar"
                  onClick={() => {
                    const codigo = codigoVisualizando ?? form.codigoLocatario;
                    if (!codigo) {
                      alert("Código do locatário inválido para editar.");
                      return;
                    }
                    setEditandoCodigo(codigo);
                    setModoVisualizacao(false);
                  }}
                >
                  <FaEdit /> Editar
                </button>
              )}

              {(!modoVisualizacao || editandoCodigo) && (
                <button type="submit" className="btn-cadastrar">
                  <FaSave /> {editandoCodigo ? "Atualizar" : "Cadastrar"}
                </button>
              )}

              {(modoVisualizacao || editandoCodigo) && (
                <button type="button" className="btn-acao" onClick={resetForm}>
                  <FaTimes /> Cancelar
                </button>
              )}

              {podeMostrarExcluirNoForm && podeExcluir && (
                <button type="button" className="btn-excluir" onClick={() => excluirRegistro(codigoVisualizando)}>
                  <FaTrash /> Excluir
                </button>
              )}
            </div>
          </form>
        </div>
      ) : (
        <div className="aviso-permissao-container">
          <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
        </div>
      )}
    </div>
  );
}

export default CadastroLocatarios;
